import React, { useMemo, useState } from 'react';

import { Button, Input } from '@appcharge/shared-ui';
import useLocalization from 'api/useLocalization';
import { useFormik } from 'formik';
import { LanguageDataParams } from 'views/Localization/components/types';
import { getCountriesOptions, getSupportedLanguageCodes, handleRenderCountriesList } from 'views/Localization/utils';

import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';

import CountryMultiSelect from '../../../CountryMultiSelect/CountryMultiSelect';

import { EditLanguageFormProps } from './EditLanguageForm.types';
import { editLanguageSchema } from './validation-schema';

import './style.scss';

const EditLanguageForm = ({
  selectedLanguage,
  onSave
}: EditLanguageFormProps) => {
  const [selectedCountries, setSelectedCountries] = useState<string[]>(
    selectedLanguage?.country ?? []
  );

  const countriesOptions = useMemo(() => getCountriesOptions(), []);
  const languageCodesOptions = useMemo(() => getSupportedLanguageCodes(), []);
  const { getAddedLanguagesList } = useLocalization();

  const handleSelectCountry = (arrSelectedCountries: string[]) => {
    const newSelectedValues = arrSelectedCountries.filter(
      (item: string) => item !== ''
    );
    setFieldValue('country', newSelectedValues, true);
    setSelectedCountries(newSelectedValues);
  };

  const addedLanguagesList = getAddedLanguagesList.data.filter((item: LanguageDataParams) => item.language.toLowerCase() !== selectedLanguage?.language.toLowerCase());
  const existingLanguageCodesList = getAddedLanguagesList?.data?.filter((item: LanguageDataParams) => item.isoCode.toLowerCase() !== selectedLanguage?.isoCode.toLowerCase());

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    submitForm,
    isValid,
    setFieldValue
  } = useFormik({
    initialValues: {
      language: selectedLanguage?.language ?? '',
      country: selectedLanguage?.country ?? [],
      isEnabled: selectedLanguage?.isEnabled ?? false,
      isoCode: selectedLanguage?.isoCode ?? ''
    },
    validationSchema: editLanguageSchema(addedLanguagesList, existingLanguageCodesList),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: (values, { resetForm }) => {
      const parsedLanguageData = values.country.map((country) => ({
        language: values.language,
        country:
          (countriesOptions.find((option) => option.value === country)
            ?.key as string) ?? '',
        isEnabled: values.isEnabled,
        isoCode: values.isoCode
      }));

      onSave(parsedLanguageData);
      setSelectedCountries([]);
      resetForm();
    }
  });

  const languageCodeOptions = useMemo(() =>
    languageCodesOptions.map(opt => ({
      value: opt.value as string,
      label: opt.content as string,
      content: opt.content as string
    })),
    [languageCodesOptions]
  );

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <div className="grid grid-cols-[1.4fr_1fr_1fr_auto] gap-4 items-end max-xl:grid-cols-1">
        <div className="w-full">
          <Input
            label="Display Name"
            isRequired
            name="language"
            value={values.language}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Language Display Name"
            state={touched.language && !!errors.language ? 'error' : 'default'}
            description={(touched.language && errors.language) || ''}
          />
        </div>
        <div className="w-full">
          <InputSelectDropdown
            label="Language Code"
            options={languageCodeOptions}
            defaultSelected={languageCodeOptions.find(opt => opt.value === values.isoCode)}
            searchPlaceholder="Search..."
            placeholder="Select"
            onOptionChange={(value) => setFieldValue('isoCode', value)}
            description={(errors.isoCode as string) || ''}
            isRequired
          />
        </div>
        <div className="w-full">
          {/*  This is TODO: Task ACDEV-18482 - will be changed with reusable component from shared-ui */}
          <CountryMultiSelect
            label="Country"
            items={countriesOptions.map(opt => ({
              value: opt.value as string,
              label: opt.content as string,
              renderFunction: opt.renderFunction
            }))}
            selectedItems={selectedCountries}
            onChange={handleSelectCountry}
            searchPlaceholder="Search..."
            placeholder="Select"
            renderValue={handleRenderCountriesList}
            error={!!errors.country}
            helperText={(errors.country as string) || ''}
            fullWidth={true}
          />
        </div>
        <div className="w-full max-xl:w-fit">
          <Button
            onClick={submitForm}
            disabled={!isValid}
            type="button"
          >
            Save
          </Button>
        </div>
      </div>
    </form>
  );
};

export default EditLanguageForm;
